// R1c owner-access-fix — Shared password visibility toggle.
// - Eye icon inside the input, right-aligned; toggles input type: password ↔ text
// - a11y: aria-label swaps between "Show password" / "Hide password"
// - aria-pressed reflects state; keyboard-reachable via Tab, activatable Space/Enter
// - Resets to type="password" whenever `resetVisibilityKey` prop changes
//   (parent bumps that key on submit success/failure so plaintext isn't left visible).
import React, { forwardRef, useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "./ui/input";

const PasswordInput = forwardRef(function PasswordInput(
  { className = "", testId = "password-input", resetVisibilityKey, ...rest },
  ref
) {
  const [visible, setVisible] = useState(false);

  // Reset to hidden whenever the parent's reset key changes (submit success/failure).
  useEffect(() => {
    if (resetVisibilityKey !== undefined) setVisible(false);
  }, [resetVisibilityKey]);

  const toggle = () => setVisible((v) => !v);
  const handleKey = (e) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div className="relative">
      <Input
        ref={ref}
        type={visible ? "text" : "password"}
        className={`pr-10 ${className}`}
        data-testid={testId}
        {...rest}
      />
      <button
        type="button"
        onClick={toggle}
        onKeyDown={handleKey}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        title={visible ? "Hide password" : "Show password"}
        tabIndex={0}
        className="absolute inset-y-0 right-0 flex items-center px-2.5 text-erf-dark/60 hover:text-erf-dark focus-visible:text-erf-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-erf-gold rounded-r"
        data-testid="login-password-visibility-toggle"
      >
        {visible ? (
          <EyeOff className="w-4 h-4" aria-hidden="true" />
        ) : (
          <Eye className="w-4 h-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
});

export default PasswordInput;
